import { ElementFactory, Question, Serializer, SurveyModel } from "survey-core";

export const DIALOG_QUESTION_TYPE = "dialog";

/**
 * ⚠️ WORKAROUND: Dialog Question Element - Modal-based question renderer
 *
 * WHY THIS EXISTS:
 * Product team required questions to appear inside modal dialogs that open from buttons,
 * NOT rendered inline on the main questionnaire screen. SurveyJS does not support this -
 * all questions must be rendered in the schema flow. This custom element implements a
 * workaround where questions are stored in the schema but rendered in a modal on button
 * click instead of inline.
 *
 * WHAT BREAKS IF REMOVED:
 * - The entire Bill of Materials modal flow in PQ questionnaires would break
 * - No way to meet product requirement without this workaround
 * - SurveyJS has no native solution for this pattern
 *
 * IMPLEMENTATION:
 * - Creates an inner SurveyJS instance that renders inside a modal
 * - Button trigger instead of inline question rendering
 * - Custom validator ensures modal content is complete before navigation
 *
 * FULL DOCUMENTATION: See docs/workarounds/README.md#modal-questions-hack-dialog-element
 *
 * DO NOT "fix" or "simplify" this code without reading the full documentation!
 * Owner's words: "I had to do so much manipulation to make it work"
 */
export class QuestionDialogModel extends Question {
  private _innerSurvey: SurveyModel;
  constructor(name: string) {
    super(name);
    this._innerSurvey = new SurveyModel();
    this.addExpressionProperty("dialogTitleExpression", (obj: any, res) => {
      if (res) {
        obj.dialogTitle = res.toString();
      }
    });
    this.addExpressionProperty("highlightButton", (obj: any, res) => {
      obj.highlightButton = res;
    });
  }

  getType() {
    return DIALOG_QUESTION_TYPE;
  }

  get tsPrefix() {
    return this.getPropertyValue("tsPrefix");
  }

  set tsPrefix(tsPrefix: string) {
    this.setPropertyValue("tsPrefix", tsPrefix);
  }

  get dialogTitle() {
    return this.getPropertyValue("dialogTitle");
  }

  get innerSurvey() {
    return this._innerSurvey;
  }

  set innerSurvey(innerSurvey: SurveyModel) {
    this._innerSurvey = innerSurvey;
  }

  set dialogTitle(dialogTitle: string) {
    this.setPropertyValue("dialogTitle", dialogTitle);
  }

  get dialogTitleExpression() {
    return this.getPropertyValue("dialogTitleExpression");
  }

  set dialogTitleExpression(dialogTitleExpression: string) {
    this.setPropertyValue("dialogTitleExpression", dialogTitleExpression);
  }

  get highlightButton() {
    return this.getPropertyValue("highlightButton");
  }

  set highlightButton(highlightButton: string) {
    this.setPropertyValue("highlightButton", highlightButton);
  }
}

export function registerDialogElement() {
  ElementFactory.Instance.registerElement(DIALOG_QUESTION_TYPE, (name) => {
    return new QuestionDialogModel(name);
  });
}

export function registerDialogSerializer() {
  Serializer.addClass(
    DIALOG_QUESTION_TYPE,
    [
      {
        name: "dialogTitle",
        category: "general",
        visibleIndex: 2,
      },
      {
        name: "dialogTitleExpression",
        type: "expression",
        category: "general",
        visibleIndex: 6,
      },
      {
        name: "highlightButton",
        type: "expression",
        category: "general",
        visibleIndex: 7,
      },
      {
        name: "innerSurvey",
        type: "survey",
        category: "general",
        isSerializable: false,
      },
    ],
    () => new QuestionDialogModel(""),
    "question"
  );
}
